import React from 'react'
import { useEffect } from 'react'
import { useMap, FeatureGroup } from 'react-leaflet'
import { flatMap } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../../context/AuthContext'
import Snackbar from '@material-ui/core/Snackbar'
import Alert from '@material-ui/lab/Alert'
import L from 'leaflet'

import '@geoman-io/leaflet-geoman-free'
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css'

const drawOptions = {
  snappable: false,
  allowSelfIntersection: false,
  continueDrawing: true,
  tooltips: false,
}

const defaultMessage = 'The parcel must have a maximum of 100 hectares.'
let insertInit = {
  family: '',
  variety: '',
}
export const EditParcelGeoman = ({
  farms,
  parcel,
  dispatchAddedParcels,
  parcelGeoJson,
  setParcelGeoJson,
  clearLastVertex,
  setClearLastVertex,
  clearDraw,
  setClearDraw,
  editParcel,
  setUpdateButtonDisabled,
  farmsHectares,
  layer,
  setTotalHectares
}) => {
  const leafletContainer = useMap()
  const groupRef = React.useRef()
  const { t } = useTranslation()
  const [openAlert, setOpenAlert] = React.useState({
    open: false,
    message: t(defaultMessage),
  })

  const numberOfParcels = React.useMemo(() => {
    return farms.filter((farm) => farm.farm_parcels?.features).flatMap((farm) => farm.farm_parcels.features).length
  }, [farms])

  const {
    user,
    isClientPaying,
    isClientInFreeTrial,
    isTecnician,
    isCompanyAdmin,
    getMaxAllowedHaForSingleParcel
  } = useAuth()

  const handleUpdateGeoJson = (data) => {
    if (parcelGeoJson.features.length) {
      parcelGeoJson.features[0] = data
    } else {
      parcelGeoJson.features.push(data)
    }
  }

  const handleSplitParcel = (data) => {
    parcelGeoJson.features.push(data)
  }

  React.useEffect(() => {
    if (!editParcel) {
      groupRef.current.clearLayers()
    }
  }, [editParcel])

  React.useEffect(() => {
    if (clearLastVertex) {
      leafletContainer.pm.Draw.Polygon._removeLastVertex()
      if (leafletContainer.pm.Draw.Polygon._layer._latlngs.length === 0)
        leafletContainer.pm.enableDraw('Polygon', drawOptions)
      setClearLastVertex(false)
    }
  }, [clearLastVertex])

  React.useEffect(() => {
    if (clearDraw) {
      leafletContainer.pm.Draw.disable()
      leafletContainer.pm.enableDraw('Polygon', drawOptions)
      setClearDraw(false)
    }
  }, [clearDraw])

  React.useEffect(() => {
    groupRef.current.clearLayers() // remove old data
    // Solo una parcella (geoJson, quindi devo creare il layer)
    let parcelLayer = L.geoJSON(parcel)
    groupRef.current.addLayer(parcelLayer)
    
    // Only enable editing for technicians or company admins
    if (isTecnician() || isCompanyAdmin()) {
      parcelLayer.pm.enable()
      // enable polygon Draw Mode
      leafletContainer.pm.addControls({
        position: 'topleft',
        drawRectangle: false,
        drawPolygon: false,
        drawCircle: false,
        drawText: false,
        drawCircleMarker: false,
        drawMarker: false,
      })
    }

    if (isTecnician() || isCompanyAdmin()) {
      leafletContainer.pm.addControls({
        drawControls: true,
        editControls: false,
        optionsControls: false,
        customControls: false,
        oneBlock: false,
      })
    }

    if (isTecnician() || isCompanyAdmin()) {
      leafletContainer.on('pm:create', (e) => {
        let geoJson = e.layer.toGeoJSON()

        if (isClientInFreeTrial() && numberOfParcels >= process.env.REACT_APP_MAX_NUM_PARCELS_IN_FREE_TRIAL) {
          e.layer.remove()
          setOpenAlert({
            open: true,
            message: t(
              'Only five parcels can be added in during the free trial. Upgrade your plan to add more or contact us.'
            ),
          })
        } else {
          handleSplitParcel(geoJson)
          leafletContainer.pm.addControls({ drawControls: false })
        }
      })
    }

    if (isTecnician() || isCompanyAdmin()) {
      parcelLayer.on('pm:edit', (e) => {
        let geoJson = e.layer.toGeoJSON()
        var area = L.GeometryUtil.geodesicArea(e.layer.getLatLngs()[0]) / 10000;
        setTotalHectares(area)

        // Check size limit in all cases
        if (area > getMaxAllowedHaForSingleParcel()) {
          setUpdateButtonDisabled(true)
          setOpenAlert({
            open: true,
            message: t('The parcel must have a maximum of 100 hectares.', { maxHaForSingleParcel: getMaxAllowedHaForSingleParcel() }),
          })
        } else if (!layer) {
          setUpdateButtonDisabled(false);
          handleUpdateGeoJson(geoJson)
        } else {
          if (isClientPaying() && (farmsHectares - parseFloat(parcel.properties.hectares)) + area > user.profile.tot_hectares) {
            setUpdateButtonDisabled(true);
            setOpenAlert({ open: true, message: t('Some parcels have not been included as they exceed your hectares quota.') });
          } else {
            setUpdateButtonDisabled(false);
            handleUpdateGeoJson(geoJson)
          }
        }
      })
    }

    // Add Controllo tot ettari
    // Salvare senza riaggiornare come Farm Name oppure Il parcel Name qui
    try {
      leafletContainer.fitBounds(groupRef.current.getBounds())
    } catch (error) {
      console.error(error)
    }
  }, [parcel])

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setOpenAlert({ open: false, message: defaultMessage })
  }

  return (
    <>
      <FeatureGroup id="draw-parcels-layer" ref={groupRef}></FeatureGroup>
      <Snackbar
        open={openAlert.open}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert severity="warning" onClose={handleClose}>
          {t(openAlert.message)}
        </Alert>
      </Snackbar>
    </>
  )
}
